/**
 * Tela de entrada (e-mail e senha) e o primeiro acesso.
 * - Primeiro acesso: o sistema acabou de ser publicado e ainda não tem usuário.
 *   Quem publicou digita o código de instalação (saida/codigo-de-instalacao-*.txt)
 *   e cria a conta de administrador. Depois disso esta parte some.
 * - Os outros usuários são criados na tela Sistema → Usuários (com senha
 *   provisória) ou pelo terminal: npm run criar-usuario -- --email=... --nome="..."
 *
 * Lê: api.instalacao, api.entrar e api.instalar.
 * Usado por: App.tsx (quando ninguém está logado).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Aviso, ErroNaTela } from '../componentes/basicos.tsx';
import { api, ErroApi } from '../dados/api.ts';
import type { Usuario } from '../dominio/tipos.ts';

interface Props {
  aoEntrar: (usuario: Usuario) => void;
  logo: string | null;
  /** A sessão acabou sozinha (tempo sem uso, ou a senha mudou): avisa por que voltou para cá. */
  sessaoTerminou?: boolean;
}

export function Entrar({ aoEntrar, logo, sessaoTerminou = false }: Props) {
  const [instalacao, setInstalacao] = useState<{ precisaInstalar: boolean; podeInstalar: boolean } | null>(null);

  useEffect(() => {
    api
      .instalacao()
      .then(setInstalacao)
      .catch(() => setInstalacao({ precisaInstalar: false, podeInstalar: false }));
  }, []);

  return (
    <main className="tela-entrar">
      <div className="cartao cartao-entrar">
        <div>
          {logo ? <img className="logo-imagem logo-entrar" src={logo} alt="Aliança" /> : <span className="logo">aliança</span>}
          <p className="subtitulo">Metas de vendas · acesso da gerência</p>
        </div>
        {instalacao === null ? (
          <p className="texto-apagado">Carregando…</p>
        ) : instalacao.precisaInstalar ? (
          instalacao.podeInstalar ? (
            <PrimeiroAcesso aoEntrar={aoEntrar} aoJaInstalado={() => setInstalacao({ precisaInstalar: false, podeInstalar: true })} />
          ) : (
            <Aviso tipo="alerta">Este servidor ainda não tem usuários nem código de instalação. Crie o primeiro usuário pelo terminal: npm run criar-usuario.</Aviso>
          )
        ) : (
          <>
            {sessaoTerminou ? (
              <Aviso tipo="info">Sua sessão terminou (muito tempo sem uso, ou a senha foi trocada). Entre de novo; o texto de um relato que estava escrevendo foi guardado.</Aviso>
            ) : null}
            <FormularioDeEntrada aoEntrar={aoEntrar} />
          </>
        )}
      </div>
    </main>
  );
}

function FormularioDeEntrada({ aoEntrar }: { aoEntrar: (usuario: Usuario) => void }) {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<ErroApi | null>(null);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    setEnviando(true);
    setErro(null);
    try {
      const { usuario } = await api.entrar(email, senha);
      aoEntrar(usuario);
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null));
      setEnviando(false);
    }
  }

  return (
    <form className="pilha" onSubmit={enviar} method="post">
      <label>
        E-mail
        <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label>
        Senha
        <input type="password" autoComplete="current-password" required value={senha} onChange={(e) => setSenha(e.target.value)} />
      </label>
      {erro ? <ErroNaTela erro={erro} /> : null}
      <button type="submit" className="botao" disabled={enviando}>
        {enviando ? 'Entrando…' : 'Entrar'}
      </button>
      <p className="texto-apagado texto-pequeno">Esqueceu a senha? Peça para o administrador criar uma senha provisória (tela Sistema).</p>
    </form>
  );
}

function PrimeiroAcesso({ aoEntrar, aoJaInstalado }: { aoEntrar: (usuario: Usuario) => void; aoJaInstalado: () => void }) {
  const [campos, setCampos] = useState({ codigo: '', nome: '', email: '', senha: '', repetir: '' });
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<ErroApi | null>(null);
  const mudar = (campo: keyof typeof campos) => (e: { target: { value: string } }) => setCampos((atual) => ({ ...atual, [campo]: e.target.value }));
  const senhasDiferentes = campos.repetir !== '' && campos.senha !== campos.repetir;

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (campos.senha !== campos.repetir) return;
    setEnviando(true);
    setErro(null);
    try {
      const { usuario } = await api.instalar({ codigo: campos.codigo, nome: campos.nome, email: campos.email, senha: campos.senha });
      aoEntrar(usuario);
    } catch (falha) {
      // Alguém já instalou (API-409): mostra a entrada normal.
      if (falha instanceof ErroApi && falha.codigo === 'API-409') {
        aoJaInstalado();
        return;
      }
      setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null));
      setEnviando(false);
    }
  }

  return (
    <form className="pilha" onSubmit={enviar} method="post">
      <Aviso tipo="info">Primeiro acesso: crie a conta de administrador com o código de instalação que veio com o pacote do sistema.</Aviso>
      <label>
        Código de instalação
        <input autoComplete="off" required value={campos.codigo} onChange={mudar('codigo')} placeholder="XXXXX-XXXXX-XXXXX-XXXXX" />
      </label>
      <label>
        Seu nome
        <input autoComplete="name" required value={campos.nome} onChange={mudar('nome')} />
      </label>
      <label>
        Seu e-mail
        <input type="email" autoComplete="username" required value={campos.email} onChange={mudar('email')} />
      </label>
      <label>
        Crie uma senha (mínimo 10 caracteres)
        <input type="password" autoComplete="new-password" required minLength={10} value={campos.senha} onChange={mudar('senha')} />
      </label>
      <label>
        Repita a senha
        <input type="password" autoComplete="new-password" required value={campos.repetir} onChange={mudar('repetir')} />
      </label>
      {senhasDiferentes ? <p className="mensagem-erro">As senhas não são iguais.</p> : null}
      {erro ? <ErroNaTela erro={erro} /> : null}
      <button type="submit" className="botao" disabled={enviando || senhasDiferentes}>
        {enviando ? 'Criando…' : 'Criar administrador e entrar'}
      </button>
    </form>
  );
}
